import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import MeasureLinkWizard from '../components/MeasureLinkWizard';
import { RISK_LEVELS, getLevelConfig, RiskLevelIndicator, ControlCard, ActionCard } from '../components/risks/risk-components';
import { RiskFormDialog, ControlFormDialog, ActionFormDialog } from '../components/risks/RiskFormDialogs';
import { PageToolbar, PageKpiStrip, PageFilterBar, PageContext, GuidedAction, SeverityBadge, StatusBadge, SeverityChip, AllChip, DetailDrawer, SortableHeader, StaticHeader, Td, Tr, DataTableWrapper, TableHead, ResultsBar, PageShell, SectionTabs, SECTIONS } from '../components/enterprise';
import { SEVERITY_LIST, CONTROLS } from '../lib/design-tokens';
import {
  Shield, ShieldAlert, ShieldCheck, ChevronRight, Pencil, AlertTriangle,
  Target, CheckCircle2, Clock, ArrowUpDown, X, Plus, RefreshCw,
  ClipboardList, Info, Lightbulb, Flame
} from 'lucide-react';

/* ─── Main Page ─── */
const RisksPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({ kpis: {}, trend: [], summary: [], risks: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [measureFilter, setMeasureFilter] = useState(null);
  const [ownerFilter, setOwnerFilter] = useState(null);
  const [sortField, setSortField] = useState('risk_level');
  const [sortDir, setSortDir] = useState('desc');
  const [riskDetailPopup, setRiskDetailPopup] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editRisk, setEditRisk] = useState(null);
  const [editControl, setEditControl] = useState(null);
  const [editAction, setEditAction] = useState(null);
  const [editParentRisk, setEditParentRisk] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const [measureWizardRisk, setMeasureWizardRisk] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setFetchError(false);
    try {
      const res = await api.get('/ggrc/risks/dashboard');
      // Normaliseer Engelse risk_levels naar Nederlands
      const LEVEL_MAP = { critical: 'kritiek', high: 'hoog', medium: 'midden', low: 'laag', minimal: 'miniem' };
      const data = res.data;
      if (data.risks) {
        data.risks = data.risks.map(r => ({
          ...r,
          risk_level: LEVEL_MAP[r.risk_level] || r.risk_level || 'laag'
        }));
      }
      setDashboard(data);
    } catch (e) {
      console.error('Failed to fetch risk dashboard:', e);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // Severity counts
  const severityCounts = useMemo(() => {
    const risks = dashboard.risks || [];
    return RISK_LEVELS.reduce((acc, l) => {
      acc[l.key] = risks.filter(r => r.risk_level === l.key).length;
      return acc;
    }, {});
  }, [dashboard.risks]);

  // Filtering & sorting
  const filteredRisks = useMemo(() => {
    let risks = dashboard.risks || [];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      risks = risks.filter(r => r.title?.toLowerCase().includes(q) || r.owner_name?.toLowerCase().includes(q) || r.owner?.toLowerCase().includes(q) || r.risk_id?.toLowerCase().includes(q));
    }
    if (levelFilter) risks = risks.filter(r => r.risk_level === levelFilter);
    if (statusFilter) risks = risks.filter(r => (r.status || 'open') === statusFilter);
    if (measureFilter === 'without') risks = risks.filter(r => (r.controls_count || 0) === 0 && (r.actions_count || 0) === 0);
    else if (measureFilter === 'with') risks = risks.filter(r => (r.controls_count || 0) > 0 || (r.actions_count || 0) > 0);
    if (ownerFilter) risks = risks.filter(r => (r.owner_name || '') === ownerFilter);

    const levelOrder = { kritiek: 5, hoog: 4, midden: 3, laag: 2, miniem: 1 };
    risks.sort((a, b) => {
      if (sortField === 'risk_level') {
        const va = levelOrder[a.risk_level] || 0;
        const vb = levelOrder[b.risk_level] || 0;
        return sortDir === 'desc' ? vb - va : va - vb;
      }
      if (sortField === 'score') {
        const va = (a.probability || 3) * (a.impact || 3);
        const vb = (b.probability || 3) * (b.impact || 3);
        return sortDir === 'desc' ? vb - va : va - vb;
      }
      let va = a[sortField] || '', vb = b[sortField] || '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return risks;
  }, [dashboard.risks, searchQuery, levelFilter, statusFilter, measureFilter, ownerFilter, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const hasActiveFilters = searchQuery || levelFilter || statusFilter || measureFilter || ownerFilter;

  const clearFilters = () => {
    setSearchQuery(''); setLevelFilter(null); setStatusFilter(null); setMeasureFilter(null); setOwnerFilter(null);
  };

  // Stats
  const totalRisks = dashboard.risks || [];
  const actionStats = useMemo(() => {
    const risks = dashboard.risks || [];
    return {
      noMeasure: risks.filter(r => (r.controls_count || 0) === 0 && (r.actions_count || 0) === 0).length,
      noOwner: risks.filter(r => !r.owner_name && !r.owner).length,
      openActions: risks.filter(r => (r.actions || []).some(a => a.status === 'Open')).length,
      highCritical: risks.filter(r => r.risk_level === 'hoog' || r.risk_level === 'kritiek').length,
    };
  }, [dashboard.risks]);

  // Most urgent risk for guided action
  const urgentRisk = useMemo(() => {
    const risks = dashboard.risks || [];
    const levelOrder = { kritiek: 5, hoog: 4, midden: 3, laag: 2, miniem: 1 };
    return [...risks]
      .filter(r => r.status === 'open' || !r.status)
      .sort((a, b) => {
        const la = levelOrder[a.risk_level] || 0, lb = levelOrder[b.risk_level] || 0;
        if (la !== lb) return lb - la;
        return ((b.probability || 3) * (b.impact || 3)) - ((a.probability || 3) * (a.impact || 3));
      })[0];
  }, [dashboard.risks]);

  // Row selection
  const toggleRowSelect = (id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedRows.size === filteredRisks.length) setSelectedRows(new Set());
    else setSelectedRows(new Set(filteredRisks.map(r => r.id)));
  };

  // CRUD
  const handleSaveRisk = async (riskData) => {
    try {
      if (editRisk?.id) await api.patch(`/ggrc/risks/${editRisk.id}`, riskData);
      else await api.post('/ggrc/risks', riskData);
      setShowAddDialog(false); setEditRisk(null); fetchDashboard();
    } catch (e) { console.error('Save risk failed:', e); }
  };
  const handleDeleteRisk = async () => {
    toast.error("Risico's mogen niet worden verwijderd. Wijzig de status naar 'accepted' of 'closed'.");
  };
  const handleSaveControl = async (control) => {
    if (!editParentRisk) return;
    const risk = dashboard.risks.find(r => r.id === editParentRisk);
    if (!risk) return;
    let controls = [...(risk.controls || [])];
    const idx = controls.findIndex(c => c.id === control.id);
    if (idx >= 0) controls[idx] = control;
    else controls.push({ ...control, id: `ctrl-${Date.now()}` });
    try {
      await api.patch(`/ggrc/risks/${editParentRisk}/controls`, { controls });
      setEditControl(null); setEditParentRisk(null); fetchDashboard();
    } catch (e) { console.error('Save control failed:', e); }
  };
  const handleSaveAction = async (action) => {
    if (!editParentRisk) return;
    const risk = dashboard.risks.find(r => r.id === editParentRisk);
    if (!risk) return;
    let actions = [...(risk.actions || [])];
    const idx = actions.findIndex(a => a.id === action.id);
    if (idx >= 0) actions[idx] = action;
    else actions.push({ ...action, id: `act-${Date.now()}` });
    try {
      await api.patch(`/ggrc/risks/${editParentRisk}/actions`, { actions });
      setEditAction(null); setEditParentRisk(null); fetchDashboard();
    } catch (e) { console.error('Save action failed:', e); }
  };
  const handleSaveMeasureFromWizard = async (formData) => {
    try {
      const measurePayload = {
        name: formData.title, description: formData.description || '', domain: formData.domain || 'process',
        impact: formData.impact || 3, complexity: formData.complexity || 'medium',
        status: (formData.status || 'Gedefinieerd').toLowerCase() === 'gedefinieerd' ? 'planned' : 'implemented',
        priority: formData.priority || 'medium', owner: formData.owner || '',
        deadline: formData.deadline || '', linked_risk_id: formData.linked_risk_id || '',
      };
      await api.post('/ggrc/measures', measurePayload);
      toast.success(`Maatregel "${formData.title}" aangemaakt en gekoppeld`);
      fetchDashboard();
    } catch { toast.error('Maatregel aanmaken mislukt'); }
  };

  // Loading state
  if (loading) {
    return <PageShell section="risicos" testId="risks-page" loading />;
  }

  // Error state
  if (fetchError) {
    return (
      <PageShell section="risicos" testId="risks-page">
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="risks-error-state">
          <AlertTriangle className="h-10 w-10 text-red-400 mb-3" strokeWidth={1.5} />
          <h2 className="text-sm font-semibold text-neutral-800 mb-1">Laden mislukt</h2>
          <p className="text-xs text-neutral-500 mb-4 max-w-sm">De risico-gegevens konden niet worden geladen.</p>
          <Button onClick={fetchDashboard} size="sm" className="bg-[#111827] text-white hover:bg-neutral-800 text-xs">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />Opnieuw
          </Button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell section="risicos" testId="risks-page">
        <div className={`flex transition-all`}>
          {/* Main Content */}
          <div className={`flex-1 min-w-0 ${riskDetailPopup ? 'mr-0' : ''}`}>
            {/* Page Toolbar */}
            <PageToolbar
              title="Risico's"
              subtitle="Beheer risico's voor de beveiliging van je organisatie."
              count={totalRisks.length}
              onRefresh={fetchDashboard}
              onExport={() => {}}
              onAdd={() => { setEditRisk(null); setShowAddDialog(true); }}
              addLabel="Risico toevoegen"
              testId="risks"
            />

            {/* KPI Strip */}
            {totalRisks.length > 0 && (
              <PageKpiStrip
                testId="risk-kpi"
                items={[
                  { label: 'Hoog / Kritiek', value: actionStats.highCritical, color: actionStats.highCritical > 0 ? 'text-red-600' : 'text-neutral-800', sub: 'vereist directe aandacht' },
                  { label: 'Zonder maatregel', value: actionStats.noMeasure, color: actionStats.noMeasure > 0 ? 'text-orange-600' : 'text-neutral-800', sub: 'onbeschermd' },
                  { label: 'Zonder eigenaar', value: actionStats.noOwner, color: actionStats.noOwner > 0 ? 'text-amber-600' : 'text-neutral-800', sub: 'niet toegewezen' },
                  { label: 'Opgelost', value: `${totalRisks.length > 0 ? Math.round(((totalRisks.length - actionStats.noMeasure) / totalRisks.length) * 100) : 0}%`, color: 'text-emerald-600', sub: 'met maatregel' },
                ]}
              />
            )}

            {/* Page Context + Guided Action */}
            {totalRisks.length > 0 && (
              <>
                <PageContext
                  description="Hier staan de risico's die uit je nulmeting, kwetsbaarheden en handmatige invoer voortkomen."
                  insight={
                    actionStats.highCritical > 0
                      ? `${actionStats.highCritical} risico's vereisen directe aandacht`
                      : actionStats.noMeasure > 0
                        ? `${actionStats.noMeasure} risico's zonder maatregel`
                        : null
                  }
                  insightType={actionStats.highCritical > 0 ? 'critical' : 'warning'}
                  testId="risks-context"
                />
                {urgentRisk && (actionStats.highCritical > 0 || actionStats.noMeasure > 0) && (
                  <GuidedAction
                    title={`Beoordeel "${urgentRisk.title}"`}
                    description={
                      (urgentRisk.risk_level === 'kritiek' || urgentRisk.risk_level === 'hoog')
                        ? `Dit is een ${urgentRisk.risk_level} risico${(urgentRisk.controls_count || 0) === 0 ? ' zonder gekoppelde maatregel' : ''}. Door dit risico te beoordelen bepaal je de impact, eigenaar en aanbevolen oplossing.`
                        : `Dit risico heeft prioriteit op basis van ernst en status. Beoordeel het om je beveiligingshouding te verbeteren.`
                    }
                    actionLabel="Risico beoordelen"
                    onAction={() => setRiskDetailPopup(urgentRisk)}
                    duration="Ongeveer 5 minuten"
                    severity={urgentRisk.risk_level === 'kritiek' ? 'critical' : urgentRisk.risk_level === 'hoog' ? 'warning' : 'info'}
                    icon={ShieldAlert}
                    secondaryLabel="Later beslissen"
                    onSecondary={() => {}}
                    testId="risks-guided-action"
                  />
                )}
              </>
            )}

            {/* Filters */}
            <PageFilterBar
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Zoek op naam, eigenaar of ID..."
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              activeFilterCount={[statusFilter, measureFilter, ownerFilter].filter(Boolean).length}
              onClearAll={clearFilters}
              chipSlot={
                <>
                  <AllChip count={totalRisks.length} active={!levelFilter} onClick={() => setLevelFilter(null)} />
                  {SEVERITY_LIST.filter(l => severityCounts[l.key] > 0).map(l => (
                    <SeverityChip key={l.key} level={l.key} count={severityCounts[l.key]} active={levelFilter === l.key} onClick={() => setLevelFilter(levelFilter === l.key ? null : l.key)} />
                  ))}
                </>
              }
              chips={[
                ...(levelFilter ? [{ label: 'Ernst', value: getLevelConfig(levelFilter).label, onRemove: () => setLevelFilter(null) }] : []),
                ...(statusFilter ? [{ label: 'Status', value: statusFilter, onRemove: () => setStatusFilter(null) }] : []),
                ...(measureFilter ? [{ label: 'Maatregel', value: measureFilter === 'without' ? 'Zonder' : 'Met', onRemove: () => setMeasureFilter(null) }] : []),
                ...(ownerFilter ? [{ label: 'Eigenaar', value: ownerFilter, onRemove: () => setOwnerFilter(null) }] : []),
                ...(searchQuery ? [{ label: 'Zoek', value: searchQuery, onRemove: () => setSearchQuery('') }] : []),
              ]}
              testId="risk"
            >
              {/* Extended filters */}
              <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? null : v)}>
                <SelectTrigger className={`w-40 ${CONTROLS.select} border-neutral-200`} data-testid="risk-status-filter"><SelectValue placeholder="Alle statussen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle statussen</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="mitigating">In behandeling</SelectItem>
                  <SelectItem value="accepted">Geaccepteerd</SelectItem>
                  <SelectItem value="closed">Gesloten</SelectItem>
                </SelectContent>
              </Select>
              <Select value={measureFilter || 'all'} onValueChange={(v) => setMeasureFilter(v === 'all' ? null : v)}>
                <SelectTrigger className={`w-44 ${CONTROLS.select} border-neutral-200`} data-testid="risk-measure-filter"><SelectValue placeholder="Alle maatregelen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle maatregelen</SelectItem>
                  <SelectItem value="without">Zonder maatregel</SelectItem>
                  <SelectItem value="with">Met maatregel</SelectItem>
                </SelectContent>
              </Select>
              {(dashboard.owners || []).length > 0 && (
                <Select value={ownerFilter || 'all'} onValueChange={(v) => setOwnerFilter(v === 'all' ? null : v)}>
                  <SelectTrigger className={`w-44 ${CONTROLS.select} border-neutral-200`} data-testid="risk-owner-filter"><SelectValue placeholder="Alle eigenaren" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle eigenaren</SelectItem>
                    {(dashboard.owners || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </PageFilterBar>

            {/* Bulk actions bar */}
            {selectedRows.size > 0 && (
              <div className="px-6 py-2.5 bg-blue-50 border-b border-blue-200 flex items-center gap-3" data-testid="bulk-actions-bar">
                <span className="text-sm font-semibold text-blue-700">{selectedRows.size} geselecteerd</span>
                <Button variant="outline" className="h-9 text-xs border-blue-200 text-blue-700 bg-white">Status wijzigen</Button>
                <Button variant="outline" className="h-9 text-xs border-blue-200 text-blue-700 bg-white">Eigenaar toewijzen</Button>
                <button onClick={() => setSelectedRows(new Set())} className="text-xs text-blue-600 hover:text-blue-800 ml-auto">Deselecteer alles</button>
              </div>
            )}

            {/* Data Table */}
            {filteredRisks.length > 0 ? (
              <DataTableWrapper testId="risk-table">
                <TableHead>
                  <th className="w-12 px-4 py-3">
                    <input type="checkbox" checked={selectedRows.size === filteredRisks.length && filteredRisks.length > 0} onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500 cursor-pointer" data-testid="select-all-checkbox" />
                  </th>
                  <SortableHeader label="Risico" field="title" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <SortableHeader label="Ernst" field="risk_level" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="w-28" />
                  <SortableHeader label="Status" field="status" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="w-32" />
                  <SortableHeader label="Score" field="score" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="w-24" />
                  <StaticHeader label="Eigenaar" className="w-36" />
                  <StaticHeader label="Maatr." className="w-20 text-center" />
                  <th className="w-8 px-2" />
                </TableHead>
                <tbody>
                  {filteredRisks.map((risk) => {
                    const measureCount = (risk.controls_count || 0) + (risk.actions_count || 0);
                    const isSelected = riskDetailPopup?.id === risk.id;
                    const score = (risk.probability || 3) * (risk.impact || 3);
                    const isChecked = selectedRows.has(risk.id);
                    return (
                      <Tr key={risk.id} active={isSelected} selected={isChecked} onClick={() => setRiskDetailPopup(risk)} testId={`risk-row-${risk.id}`}>
                        <Td className="w-12" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={isChecked} onChange={() => toggleRowSelect(risk.id)}
                            className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500 cursor-pointer" />
                        </Td>
                        <Td>
                          <div className="flex items-center gap-2.5">
                            <span className="text-xs font-mono text-[#6B7280] shrink-0">{risk.risk_id || 'R-???'}</span>
                            <span className="text-sm font-medium text-[#111827] truncate max-w-[300px]">{risk.title}</span>
                          </div>
                        </Td>
                        <Td><SeverityBadge level={risk.risk_level} /></Td>
                        <Td><StatusBadge status={risk.status || 'open'} /></Td>
                        <Td>
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-bold text-[#111827]">{score}</span>
                            <span className="text-xs text-[#6B7280]">({risk.probability || 3}x{risk.impact || 3})</span>
                          </div>
                        </Td>
                        <Td>
                          <span className={`text-sm ${risk.owner_name || risk.owner ? 'text-[#111827]' : 'text-[#9CA3AF] italic'}`}>
                            {risk.owner_name || risk.owner || 'Niet toegewezen'}
                          </span>
                        </Td>
                        <Td align="center">
                          <span className={`text-sm font-bold ${measureCount > 0 ? 'text-[#047857]' : 'text-[#9CA3AF]'}`}>{measureCount}</span>
                        </Td>
                        <td className="px-2 py-3"><ChevronRight className="h-4 w-4 text-neutral-300" strokeWidth={1.5} /></td>
                      </Tr>
                    );
                  })}
                </tbody>
              </DataTableWrapper>
            ) : totalRisks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-risks">
                <div className="h-14 w-14 rounded-xl bg-neutral-100 flex items-center justify-center mb-4">
                  <ShieldAlert className="h-7 w-7 text-neutral-400" strokeWidth={1.5} />
                </div>
                <h2 className="text-base font-semibold text-neutral-800">Geen risico's gevonden</h2>
                <p className="text-sm text-neutral-500 mt-1 max-w-sm">Start een nulmeting om automatisch risico's te identificeren, of voeg er handmatig een toe.</p>
                <div className="flex gap-2 mt-5">
                  <Button variant="outline" className={CONTROLS.primary} onClick={() => navigate('/assessment')}>Nulmeting starten</Button>
                  <Button className={`${CONTROLS.primary} bg-[#111827] text-white`} onClick={() => { setEditRisk(null); setShowAddDialog(true); }}>
                    <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} />Toevoegen
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="no-results">
                <h2 className="text-base font-semibold text-neutral-700">Geen resultaten</h2>
                <p className="text-sm text-neutral-500 mt-1">Pas je filters aan of wis ze om alle risico's te zien.</p>
                <Button variant="outline" className={`${CONTROLS.primary} mt-4`} onClick={clearFilters}>Filters wissen</Button>
              </div>
            )}

            {/* Results count */}
            {filteredRisks.length > 0 && (
              <ResultsBar
                shown={filteredRisks.length}
                total={totalRisks.length}
                sortLabel={sortField === 'risk_level' ? 'Ernst' : sortField === 'score' ? 'Score' : sortField === 'title' ? 'Naam' : sortField}
                sortDir={sortDir}
              />
            )}
          </div>

          {/* Detail Drawer */}
          {riskDetailPopup && (
            <DetailDrawer
              open={!!riskDetailPopup}
              onClose={() => setRiskDetailPopup(null)}
              testId="risk-detail-drawer"
              header={
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded">{riskDetailPopup.risk_id || 'R-???'}</span>
                    <SeverityBadge level={riskDetailPopup.risk_level} />
                    <StatusBadge status={riskDetailPopup.status || 'open'} />
                  </div>
                  <h2 className="text-sm font-bold text-neutral-900 leading-tight">{riskDetailPopup.title}</h2>
                </>
              }
              footer={
                <>
                  <Button variant="ghost" size="sm" className="text-[10px] text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2" onClick={handleDeleteRisk}>Verwijderen</Button>
                  <div className="flex-1" />
                  <Button variant="outline" size="sm" className="h-7 text-[10px] border-neutral-200" onClick={() => { setRiskDetailPopup(null); setMeasureWizardRisk(riskDetailPopup); }} data-testid="drawer-link-measure">
                    <Shield className="h-3 w-3 mr-1" strokeWidth={1.5} />Koppel
                  </Button>
                  <Button size="sm" className="h-7 text-[10px] bg-[#111827] text-white hover:bg-neutral-800" onClick={() => { setEditRisk(riskDetailPopup); setShowAddDialog(true); setRiskDetailPopup(null); }} data-testid="drawer-edit-risk">
                    <Pencil className="h-3 w-3 mr-1" strokeWidth={1.5} />Bewerk
                  </Button>
                </>
              }
            >
              <RiskDrawerContent
                risk={riskDetailPopup}
                api={api}
                onAddControl={() => { setEditControl({}); setEditParentRisk(riskDetailPopup.id); }}
                onEditControl={(ctrl) => { setEditControl(ctrl); setEditParentRisk(riskDetailPopup.id); }}
                onAddAction={() => { setEditAction({}); setEditParentRisk(riskDetailPopup.id); }}
                onEditAction={(act) => { setEditAction(act); setEditParentRisk(riskDetailPopup.id); }}
              />
            </DetailDrawer>
          )}
        </div>

      <MeasureLinkWizard open={!!measureWizardRisk} onClose={() => setMeasureWizardRisk(null)} risk={measureWizardRisk} onSave={handleSaveMeasureFromWizard} />
      <RiskFormDialog open={showAddDialog} onClose={() => { setShowAddDialog(false); setEditRisk(null); }} onSave={handleSaveRisk} risk={editRisk} />
      <ControlFormDialog open={!!editControl} onClose={() => { setEditControl(null); setEditParentRisk(null); }} onSave={handleSaveControl} control={editControl} />
      <ActionFormDialog open={!!editAction} onClose={() => { setEditAction(null); setEditParentRisk(null); }} onSave={handleSaveAction} action={editAction} />
    </PageShell>
  );
};

/* ─── Detail Drawer ─── */
const getNextStep = (risk) => {
  const hasControls = (risk.controls || []).length > 0;
  const hasActions = (risk.actions || []).length > 0;
  const isHighRisk = risk.risk_level === 'hoog' || risk.risk_level === 'kritiek';
  const openActions = (risk.actions || []).filter(a => a.status === 'Open').length;
  const noOwner = !risk.owner_name && !risk.owner;
  if (isHighRisk && !hasControls && !hasActions) return { text: 'Directe aandacht: voeg een control of actie toe', action: 'control', color: 'red', icon: AlertTriangle };
  if (!hasControls) return { text: 'Voeg een control toe om dit risico te beheersen', action: 'control', color: 'blue', icon: ShieldCheck };
  if (!hasActions) return { text: 'Maak een actie aan om de mitigatie te starten', action: 'action', color: 'violet', icon: ClipboardList };
  if (noOwner) return { text: 'Wijs een eigenaar toe', action: 'edit', color: 'amber', icon: Target };
  if (openActions > 0) return { text: `${openActions} open actie${openActions > 1 ? 's' : ''}, werk aan de voortgang`, action: 'none', color: 'amber', icon: Clock };
  return { text: 'Risico is goed gedekt', action: 'none', color: 'green', icon: CheckCircle2 };
};

/* ─── Risk Drawer Content (body only — header/footer via DetailDrawer) ─── */
const RiskDrawerContent = ({ risk, api, onAddControl, onEditControl, onAddAction, onEditAction }) => {
  const [chainData, setChainData] = useState(null);
  const [showCoach, setShowCoach] = useState(false);

  useEffect(() => {
    if (risk?.id && api) {
      api.get(`/ggrc/chain/${risk.id}`).then(res => setChainData(res.data)).catch(() => setChainData(null));
    }
  }, [risk?.id, api]);

  if (!risk) return null;
  const nextStep = getNextStep(risk);
  const StepIcon = nextStep.icon;
  const score = (risk.probability || 3) * (risk.impact || 3);
  const hasOwner = !!(risk.owner_name || risk.owner);

  const stepColors = {
    red: 'text-[#B91C1C] bg-[#FEF2F2] border-[#FECACA]',
    blue: 'text-[#1D4ED8] bg-[#EFF6FF] border-[#BFDBFE]',
    violet: 'text-[#6D28D9] bg-[#F5F3FF] border-[#DDD6FE]',
    amber: 'text-[#92400E] bg-[#FFFBEB] border-[#FDE68A]',
    green: 'text-[#047857] bg-[#ECFDF5] border-[#A7F3D0]',
  };

  return (
    <>
      {/* Next step */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${stepColors[nextStep.color]}`} data-testid="drawer-next-step">
        <StepIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
        <span className="font-medium flex-1">{nextStep.text}</span>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Score', value: score, color: score >= 15 ? 'text-[#B91C1C]' : score >= 10 ? 'text-[#92400E]' : 'text-[#111827]' },
          { label: 'Kans', value: risk.probability || 3, color: 'text-[#111827]' },
          { label: 'Impact', value: risk.impact || 3, color: 'text-[#111827]' },
        ].map((m, i) => (
          <div key={i} className="p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB] text-center">
            <p className={`text-xl font-bold ${m.color}`}>{m.value}</p>
            <p className="text-xs text-[#6B7280] uppercase tracking-wide mt-0.5">{m.label}</p>
          </div>
        ))}
      </div>

      {/* Description */}
      {risk.description && (
        <div>
          <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1">Beschrijving</p>
          <p className="text-sm text-[#4B5563] leading-relaxed">{risk.description}</p>
        </div>
      )}

      {/* Owner */}
      <div className="flex items-center justify-between py-2.5 border-t border-b border-[#E5E7EB]">
        <span className="text-sm text-[#6B7280]">Eigenaar</span>
        <span className={`text-sm font-medium ${hasOwner ? 'text-[#111827]' : 'text-[#B91C1C]'}`}>
          {hasOwner ? (risk.owner_name || risk.owner) : 'Niet toegewezen'}
        </span>
      </div>

      {/* Risk Level Indicator */}
      <div className="p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
        <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Risico-indicator</p>
        <RiskLevelIndicator initial={risk.initial_risk} current={risk.current_risk} target={risk.target_risk} />
      </div>

      {/* Controls */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Controls ({(risk.controls || []).length})</p>
          <button onClick={onAddControl} className="text-xs text-[#1D4ED8] hover:text-[#1E40AF] font-medium" data-testid="drawer-add-control">+ Toevoegen</button>
        </div>
        {(risk.controls || []).length === 0 ? (
          <p className="text-sm text-[#6B7280] py-3 text-center bg-[#F9FAFB] rounded-lg border border-dashed border-[#E5E7EB]">Geen controls</p>
        ) : (
          <div className="space-y-1.5">{risk.controls.map(ctrl => <ControlCard key={ctrl.id} control={ctrl} onEdit={onEditControl} />)}</div>
        )}
      </div>

      {/* Actions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Acties ({(risk.actions || []).length})</p>
          <button onClick={onAddAction} className="text-xs text-[#1D4ED8] hover:text-[#1E40AF] font-medium" data-testid="drawer-add-action">+ Toevoegen</button>
        </div>
        {(risk.actions || []).length === 0 ? (
          <p className="text-sm text-[#6B7280] py-3 text-center bg-[#F9FAFB] rounded-lg border border-dashed border-[#E5E7EB]">Geen acties</p>
        ) : (
          <div className="space-y-1.5">{risk.actions.map(act => <ActionCard key={act.id} action={act} onEdit={onEditAction} />)}</div>
        )}
      </div>

      {/* Coach */}
      {!showCoach ? (
        <button onClick={() => setShowCoach(true)} className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1D4ED8] transition-colors" data-testid="show-coach-btn">
          <Lightbulb className="h-4 w-4" strokeWidth={1.5} /> Risico-coach tonen
        </button>
      ) : (
        <RiskCoach risk={risk} chainData={chainData} onClose={() => setShowCoach(false)} />
      )}
    </>
  );
};

/* ─── Risk Coach ─── */
const RiskCoach = ({ risk, chainData, onClose }) => {
  const prob = risk.probability || 3;
  const imp = risk.impact || 3;
  const score = prob * imp;
  const hasOwner = !!(risk.owner_name || risk.owner);
  const controlCount = (risk.controls || []).length;
  const actionCount = (risk.actions || []).length;
  const hasChain = chainData && (chainData.asset || chainData.vulnerabilities?.length > 0 || chainData.threat);

  const insights = [];
  if (score >= 20) insights.push({ type: 'critical', text: `Score ${score} (${prob}x${imp}): topprioriteit, directe aandacht vereist.` });
  else if (score >= 15) insights.push({ type: 'warning', text: `Score ${score}: serieus risico, plan deze week actie.` });
  else if (score >= 10) insights.push({ type: 'info', text: `Score ${score}: middenzone, monitor en plan verbetering.` });
  else insights.push({ type: 'ok', text: `Score ${score}: beheersbaar, periodiek herzien.` });

  if (controlCount === 0 && score >= 10) insights.push({ type: 'critical', text: 'Geen controls gekoppeld. Voeg minimaal één control toe.' });
  if (!hasOwner) insights.push({ type: 'warning', text: 'Geen eigenaar. Wijs iemand aan die verantwoordelijk is.' });

  const factors = [hasOwner, controlCount > 0, actionCount > 0, !!risk.description, hasChain];
  const confidence = factors.filter(Boolean).length;

  const iconMap = { critical: AlertTriangle, warning: Flame, info: Info, ok: CheckCircle2 };
  const colorMap = {
    critical: 'text-[#B91C1C] bg-[#FEF2F2] border-[#FECACA]',
    warning: 'text-[#92400E] bg-[#FFFBEB] border-[#FDE68A]',
    info: 'text-[#1D4ED8] bg-[#EFF6FF] border-[#BFDBFE]',
    ok: 'text-[#047857] bg-[#ECFDF5] border-[#A7F3D0]',
  };

  return (
    <div className="p-4 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB]" data-testid="risk-coach">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-[#4B5563] uppercase tracking-wider flex items-center gap-1.5">
          <Lightbulb className="h-3.5 w-3.5" strokeWidth={1.5} />Risico-coach
        </h4>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${confidence >= 4 ? 'bg-[#ECFDF5] text-[#047857]' : confidence >= 2 ? 'bg-[#FFFBEB] text-[#92400E]' : 'bg-[#FEF2F2] text-[#B91C1C]'}`}>
            {confidence}/5
          </span>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#111827]"><X className="h-3.5 w-3.5" strokeWidth={2} /></button>
        </div>
      </div>
      <div className="space-y-2">
        {insights.map((insight, i) => {
          const IIcon = iconMap[insight.type];
          return (
            <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg border ${colorMap[insight.type]}`} data-testid={`coach-insight-${i}`}>
              <IIcon className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-sm leading-relaxed">{insight.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RisksPage;
